import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Client-side auth guard - redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({
        to: "/login",
        search: {
          redirect: location.pathname,
        },
        replace: true,
      });
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex min-h-svh w-full flex-col items-center justify-center">
        <Spinner size={32} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <AuthenticatedShell />;
}

function AuthenticatedShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOverview = location.pathname === "/";
  const isAdminRoute = location.pathname === "/admin" || location.pathname.startsWith("/admin/");
  const isFilesRoute = location.pathname === "/files";
  const isPresentationEditor = /^\/presentations\/[^/]+/.test(location.pathname);
  const isPresentationsHub = location.pathname === "/presentations";
  //  Hub + editor span the viewport; other routes keep the capped shell.
  const isFullBleed = isPresentationEditor || isPresentationsHub;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((!event.metaKey && !event.ctrlKey) || event.key.toLowerCase() !== "k") {
        return;
      }

      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }

      event.preventDefault();
      void navigate({ to: "/" });
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  return (
    <div
      className={cn(
        "flex h-svh w-full flex-col overflow-hidden bg-transparent",
        isFullBleed ? "p-0" : "p-4",
      )}
    >
      <div
        className={cn(
          "mx-auto flex h-full w-full min-h-0 flex-1 flex-col",
          isFullBleed ? "max-w-none gap-0" : "max-w-[var(--app-shell-max-width)] gap-4",
        )}
      >
        <div className="flex flex-1 min-w-0 flex-col overflow-hidden bg-transparent">
          <AppErrorBoundary title="This page encountered an error">
            {isOverview ||
            isAdminRoute ||
            isFilesRoute ||
            isPresentationEditor ||
            isPresentationsHub ? (
              <main className="h-full w-full overflow-y-auto no-scrollbar">
                <div className="h-full w-full">
                  <Outlet />
                </div>
              </main>
            ) : (
              <ScrollArea className="flex-1">
                <main className="h-full min-h-full w-full">
                  <div className="h-full">
                    <Outlet />
                  </div>
                </main>
              </ScrollArea>
            )}
          </AppErrorBoundary>
        </div>
      </div>
    </div>
  );
}
